
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import DOMPurify from 'dompurify';
import OrderSummary from '@/components/shop/orderSummary.vue';
import Address from '@/components/shop/address.vue';
import Coupon from '@/components/shop/coupon.vue';
import PaymentMethods from '@/components/shop/paymentMethods.vue';
import PrimaryButton from '@/components/ui/primaryButton.vue';
import Checkbox from '@/components/ui/checkbox.vue';
import CartItem from '@/components/shop/cartItem.vue';
import ShippingLeft from '@/components/shop/shippingLeft.vue';

export default {
  name: 'Checkout',

  components: {
    OrderSummary,
    Address,
    Coupon,
    PaymentMethods,
    PrimaryButton,
    Checkbox,
    CartItem,
    ShippingLeft,
  },

  data() {
    return {
      message: null,
      paid: false,
      working: false,
      error: null,
      showPaymentMethods: false,
      paymentStatus: null,
      isRedirect: false,
      sameBilling: true,
      deliverySaved: false,
      billingSaved: false,
      zip: null,
      isStorePickup: false, // process.env.REGION === 'AO',
      showStorePickupMessage: false,
      pickupStoreMessage: '',
    };
  },

  computed: {
    ...mapState([
      'cart',
      'orderId',
      'order',
      'patientId',
      'user',
      'marketing',
      'terms',
      'mbwayPending',
      'shippingLeft',
      'calculatedZip',
      'calculatedShipping',
      'checkoutStep',
      'removedItems',
      'updatedItems',
      'couponCode',
    ]),

    ...mapGetters(['cartQuantity', 'subTotal']),

    checkoutProgress() {
      return this.checkoutStep === 4
        ? this.paid && this.isStorePickup
          ? '100%'
          : '95%'
        : `${this.checkoutStep * 25}%`;
    },

    ready() {
      return Boolean(this.patientId && this.orderId && this.order.address);
    },

    deliveryIsPossible() {
      return this.order?.shipping ? !this.order.shipping.no_sale : false;
    },

    deliveryInfo() {
      return (
        this.order?.shipping?.message || this.$t('checkout.noDeliveryInfo')
      );
    },

    termsAccepted: {
      get() {
        return this.terms;
      },
      set(value) {
        this.setTerms(value);
      },
    },

    editOrderDisabled() {
      return (
        this.isRedirect ||
        this.showPaymentMethods ||
        this.showStorePickupMessage
      );
    },

    addressSaved() {
      return this.sameBilling
        ? this.billingSaved
        : this.deliverySaved && this.billingSaved;
    },

    subscribe: {
      get() {
        return this.marketing;
      },
      set(value) {
        this.setMarketing(value);
      },
    },

    buttonEnabled() {
      return this.terms && this.addressSaved;
    },

    payButtonMessage() {
      return this.paid
        ? this.isStorePickup
          ? this.$t('checkout.store_pick_up_confirmation_return')
          : this.$t('checkout.paymentSuccess')
        : this.isStorePickup
        ? this.checkoutStep === 4
          ? this.$t('checkout.store_pick_up_confirm')
          : this.$t('checkout.store_pick_up')
        : this.$t('checkout.pay');
    },

    isCampaignPage() {
      return (this.$route.query.page || '').toLowerCase() === 'campaign';
    },
  },

  watch: {
    user(value) {
      this.updateShipping();
      if (value) {
        this.setCheckoutStep(3);
        if (this.orderId) {
          this.updateOrder();
        } else {
          this.createOrder();
        }
      }
    },

    order(value) {
      if (value && value.status > 4) {
        this.paid = true;
      }
    },

    orderId(id) {
      if (id) this.tryAutoApplyCoupon();
    },

    cartQuantity(value) {
      if (!value) {
        this.clearCart();
      }
    },

    mbwayPending(value) {
      if (!value) {
        this.working = false;
      }
    },

    error(value) {
      if (value) {
        setTimeout(() => {
          this.error = null;
        }, 12000);
      }
    },

    sameBilling(value) {
      if (value) {
        setTimeout(() => {
          this.$refs.address.changeAddress();
        }, 200);
      }
    },
  },

  async mounted() {
    this.setTerms(false);
    this.getProducts();
    this.clearRemovedItems();
    this.clearUpdatedItems();
    if (this.user) {
      this.setCheckoutStep(3);
    } else {
      this.setCheckoutStep(1);
    }
    if (this.$route.query.orderid) {
      this.handleRedirect();
    } else if (this.orderId) {
      await this.updateOrder(); // ???
    } else {
      await this.createOrder();
    }
    if (this.orderId) {
      if (this.order.status === 1 || this.order.status > 6) {
        this.$router.push(
          this.localePath({
            name: 'order-confirm-hash',
            params: { id: this.order.hash },
          })
        );
      }
      this.tryAutoApplyCoupon();
    }
  },

  beforeDestroy() {
    if (this.paid) {
      this.clearCart();
    }
  },

  methods: {
    ...mapActions(['updateOrder', 'createOrder', 'getOrder']),

    ...mapMutations([
      'clearCart',
      'setProducts',
      'setMarketing',
      'clearOrder',
      'setTerms',
      'setOrderId',
      'setOrder',
      'setMbDetails',
      'removeCartItem',
      'setCheckoutStep',
      'setCalculatedShipping',
      'setShippingLeft',
      'clearRemovedItems',
      'clearUpdatedItems',
    ]),

    handleEditOrder() {
      this.setCheckoutStep(3);
      this.showPaymentMethods = false;
      this.showStorePickupMessage = false;
    },

    async updateShipping() {
      if (this.calculatedZip) {
        try {
          const { data } = await this.$bolev.getShipping({
            zip: this.calculatedZip,
            total: this.subTotal,
          });
          this.setCalculatedShipping(data.shipping);
          this.setShippingLeft(data.left);
        } catch (err) {
          console.log('Error');
        }
      }
    },

    async handleRedirect() {
      const { data } = await this.$bolev.getOrder({
        orderId: this.$route.query.orderid,
        patientId: this.patientId,
      });
      this.setOrder(data);
      this.setOrderId(this.$route.query.orderid);
      switch (this.$route.query.state) {
        case 'pending':
        case 'forwarding':
          if (
            this.order &&
            this.order.payments.length &&
            ['multibanco', 'kwanza-ref'].includes(
              [...this.order.payments].reverse()[0].method
            )
          ) {
            const payment = [...this.order.payments].reverse()[0];
            this.setMbDetails({
              entity: payment.mb_entity,
              reference: payment.mb_reference,
              expires: payment.expires_at,
            });
            this.paymentSuccess();
          }
          break;
        case 'completed':
          this.paymentSuccess();
          break;
        case 'declined':
        case 'error':
        default:
          this.paymentFail();
      }
    },

    setDeliverySaved(value) {
      this.deliverySaved = value;
    },

    setBillingSaved(value) {
      this.billingSaved = value;
    },

    async pointsOrder() {
      this.working = true;
      try {
        const { data } = await this.$bolev.pointsOrder({
          marketing: this.marketing,
          orderId: this.order.id,
        });
        this.working = false;
        return data.success
          ? this.paymentSuccess(null)
          : this.paymentFail(null);
      } catch (e) {
        this.working = false;
        this.paymentFail(null);
      }
    },

    pay() {
      if (this.isStorePickup && this.paid) {
        this.handleNavigation();
      }
      if (
        (!this.isStorePickup && !this.showPaymentMethods) ||
        (this.isStorePickup && !this.showStorePickupMessage)
      ) {
        this.error = null;
        this.paymentStatus = null;
        if (!this.order.address) {
          this.error = this.$t('checkout.fill_in_your_details');
          return;
        }
        if (!this.terms) {
          this.error = this.$t('checkout.please_accept');
          return;
        }
        if (!this.addressSaved) {
          this.error = this.$t('checkout.pleaseSaveAddress');
          return;
        }
        if (this.order.points_payment && parseInt(this.order.price) === 0) {
          return this.pointsOrder();
        } else if (this.order.price > 0) {
          this.setCheckoutStep(4);
          this.$bolev.metaEvent({
            type: 'checkout',
            patientId: this.patientId,
          });
          this.showPaymentMethods = !this.isStorePickup;
          this.showStorePickupMessage = this.isStorePickup;
        }
      } else if (!this.isStorePickup) {
        this.working = true;
        this.$refs.payment.pay();
      } else {
        // TODO: Remove hard coded clinic id when ready
        this.working = true;
        this.$bolev
          .storeOrder({
            ...{
              orderId: this.orderId,
              clinicId: 1,
              message: DOMPurify.sanitize(this.pickupStoreMessage),
            },
          })
          .then((res) => {
            if (res.data && res.data.id && res.data.id === this.orderId) {
              this.paid = true;
              this.pickupStoreMessage = '';
            } else {
              this.error = this.$t('global.error');
            }
            this.working = false;
          })
          .catch((err) => {
            // Using global error. Change if we need to handle it better
            this.error = this.$t('global.error') || err;
            this.working = false;
          });
      }
    },

    async getProducts() {
      const { data } = await this.$wc.products({
        sku: this.cart.map((r) => r.sku),
      });
      this.setProducts(data);
    },

    paymentSuccess(data) {
      this.working = false;
      this.paid = true;
      if (data && data.msg) {
        this.paymentStatus = data.msg;
      }
      this.$router.push(
        this.localePath({
          name: 'order-confirm-hash',
          params: { hash: this.order.hash },
        })
      );
    },

    paymentFail(msg) {
      this.working = false;
      this.error = msg;
    },

    clearErrors() {
      this.error = null;
      this.paymentStatus = null;
    },

    toggleSubscribe() {
      if (!this.showPaymentMethods || !this.showStorePickupMessage) {
        this.subscribe = !this.subscribe;
      }
    },
    toggleTerms() {
      if (!this.showPaymentMethods || !this.showStorePickupMessage) {
        this.termsAccepted = !this.termsAccepted;
      }
    },

    async tryAutoApplyCoupon() {
      if (!this.orderId) return;
      if (!this.couponCode) return;

      const alreadyOnOrder =
        Array.isArray(this.order?.coupons) &&
        this.order.coupons.some(
          (c) => (c.code || '').toLowerCase() === this.couponCode.toLowerCase()
        );
      if (alreadyOnOrder) return;

      try {
        const res = await this.$bolev.coupon({
          coupon: this.couponCode,
          orderId: this.orderId,
          patientId: this.patientId,
        });
        if (!res.data.error) {
          this.setOrder(res.data.order);
        }
      } catch (e) {}
    },

    scrollTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    handleNavigation() {
      if (this.isCampaignPage) {
        this.$router.back();
        this.scrollTop();
      } else {
        this.$router.push('/');
        this.scrollTop();
      }
    },
  },

  head() {
    return {
      ...this.$seo.page('checkout', this.$route.path),
      ...{
        link: [
          {
            rel: 'stylesheet',
            href: 'https://libs.hipay.com/themes/material.css',
          },
        ],
        script: [
          {
            hid: 'hipay',
            src: 'https://libs.hipay.com/js/sdkjs.js',
            defer: true,
          },
        ],
      },
    };
  },
};
